import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'bg-[var(--primary)] text-[var(--primary-foreground)] shadow-sm hover:brightness-110 hover:-translate-y-px hover:shadow-md active:translate-y-0 active:shadow-sm',
        // DESIGN.md aligned primary with restrained gradient using accent for recovery CTAs like "Load on Canvas"
        primary:
          'bg-gradient-to-r from-[var(--accent)] to-[var(--accent-light)] text-white shadow-sm hover:brightness-105 hover:-translate-y-px hover:shadow-md active:translate-y-0 active:shadow-sm font-semibold',
        destructive:
          'bg-[var(--destructive)] text-[var(--destructive-foreground)] shadow-sm hover:brightness-110 hover:-translate-y-px hover:shadow-md active:translate-y-0',
        outline:
          'border border-[var(--primary)]/30 bg-background text-[var(--primary)] hover:bg-[var(--primary)]/5 hover:border-[var(--primary)]/50 active:bg-[var(--primary)]/10',
        secondary:
          'bg-[var(--secondary)] text-[var(--secondary-foreground)] border border-[var(--border)] hover:bg-[var(--accent)] hover:border-[var(--border)]/80 active:bg-[var(--secondary)]',
        ghost:
          'hover:bg-[var(--accent)]/50 hover:text-[var(--accent-foreground)] active:bg-[var(--accent)]/70',
        link: 'text-[var(--primary)] underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-10 px-4 py-2 min-h-[44px] min-w-[44px]',
        sm: 'h-9 rounded-md px-3 min-h-[44px] min-w-[44px]',
        lg: 'h-11 rounded-md px-8 text-base min-h-[44px] min-w-[44px]',
        icon: 'h-10 w-10 min-h-[44px] min-w-[44px]',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, children, ...props }, ref) => {
    // Safety check for asChild usage
    if (asChild && !React.isValidElement(children)) {
      console.warn('Button: asChild prop requires a valid React element as children');
      asChild = false;
    }

    const Comp = asChild ? Slot : 'button';
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props}>
        {children}
      </Comp>
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
